import { c as _c } from "react/compiler-runtime";
import { CardCollapsable, CardCollapsableProps } from 'components/Card';
import { SaveStatusCircle } from 'components/form/SaveStatusCircle';
import { Role, roleMd } from 'shared/forecast/roleTypes';
import { ComboMultiInput, ComboMultiInputProps } from 'components/form/ComboMulti';
import { P, match } from 'ts-pattern';
import { Alert, Badge, Select } from '@mantine/core';
import { twMerge } from 'tailwind-merge';
import { useQueryString } from 'utils/useQueryString';
import { z } from 'zod';
import { notNull } from 'shared/type';
import { pipe } from 'remeda';
import { isEmpty } from 'remeda';
import { mapEntries } from 'shared/collection';
import { StringNumberInput } from 'components/form/StringNumberInput';
import { WorkspaceTypes as WT } from 'shared/forecast/workspaceTypes';
import type { DbFormSubset } from 'components/hooks/useDbForm';
import type { License } from 'shared/forecast/license';
import { upperFirst } from '@mantine/hooks';
import { HiExclamation } from 'react-icons/hi';
import type { AdminUsersLimit } from './AdminUsers';
export const RoleInput = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      label: "Roles",
      placeholder: "select roles..."
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <ComboMultiInput md={roleMd} size="md" {...t0} {...props} />;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const sanitizeTitle = (title: string) => title.toLowerCase().replace(/[^a-z0-9]/g, '-');
export const useAdminSection = t0 => {
  const $ = _c(15);
  const {
    name,
    startCollapsed
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = z.object({
      adminSections: z.array(z.string()).default([]),
      adminSectionsClosed: z.array(z.string()).default([])
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    query: t2,
    setQuery
  } = useQueryString(t1);
  const {
    adminSections,
    adminSectionsClosed
  } = t2;
  let t3;
  if ($[1] !== adminSections || $[2] !== adminSectionsClosed || $[3] !== name || $[4] !== startCollapsed) {
    t3 = !adminSectionsClosed.includes(name) && (!startCollapsed || adminSections.includes(name));
    $[1] = adminSections;
    $[2] = adminSectionsClosed;
    $[3] = name;
    $[4] = startCollapsed;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const opened = t3;
  let t4;
  if ($[6] !== adminSections || $[7] !== adminSectionsClosed || $[8] !== name || $[9] !== setQuery || $[10] !== startCollapsed) {
    t4 = opened_0 => {
      setQuery({
        adminSections: pipe(opened_0 ? [...adminSections, startCollapsed ? name : undefined].filter(notNull) : adminSections.filter(s => s != name), _temp),
        adminSectionsClosed: pipe(opened_0 ? adminSectionsClosed.filter(s_0 => s_0 != name) : [...adminSectionsClosed, startCollapsed ? undefined : name].filter(notNull), _temp2)
      });
    };
    $[6] = adminSections;
    $[7] = adminSectionsClosed;
    $[8] = name;
    $[9] = setQuery;
    $[10] = startCollapsed;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== opened || $[13] !== t4) {
    t5 = {
      opened,
      onChange: t4
    };
    $[12] = opened;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export type UseAdminSection = ReturnType<typeof useAdminSection>;
export const AdminSection = t0 => {
  const $ = _c(27);
  let children;
  let classNames;
  let isDirty;
  let num;
  let props;
  let title;
  if ($[0] !== t0) {
    ({
      children,
      title,
      num,
      isDirty,
      classNames,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = classNames;
    $[3] = isDirty;
    $[4] = num;
    $[5] = props;
    $[6] = title;
  } else {
    children = $[1];
    classNames = $[2];
    isDirty = $[3];
    num = $[4];
    props = $[5];
    title = $[6];
  }
  let t1;
  if ($[7] !== title) {
    t1 = <div className="bold text-[1.1em] text-[color-mix(in_srgb,var(--mantine-color-text),transparent_50%)] grow">{title}</div>;
    $[7] = title;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== num) {
    t2 = match(num).with(P.nullish, _temp3).with(P.number, _temp4).otherwise(_temp5);
    $[9] = num;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== isDirty) {
    t3 = isDirty != null && <SaveStatusCircle isDirty={isDirty} className="ml-auto" />;
    $[11] = isDirty;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t3) {
    t4 = <div className="flex gap-2 items-center justify-between">{t1}{t2}{t3}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const t5 = classNames?.container;
  let t6;
  if ($[17] !== t5) {
    t6 = twMerge("pt-2 mb-6", t5);
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== classNames || $[20] !== t6) {
    t7 = {
      ...classNames,
      container: t6
    };
    $[19] = classNames;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== children || $[23] !== props || $[24] !== t4 || $[25] !== t7) {
    t8 = <CardCollapsable sticky={true} {...props} title={t4} classNames={t7}>{children}</CardCollapsable>;
    $[22] = children;
    $[23] = props;
    $[24] = t4;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
export const editLimitProps = ({
  limit,
  workspaceForm
}: {
  limit: AdminUsersLimit;
  workspaceForm: DbFormSubset<Pick<WT.Cfg, 'limitUsers' | 'limitParticipants'>>;
}) => {
  const wsProp = match(limit.type).with('users', () => 'limitUsers' as const).with('participants', () => 'limitParticipants' as const).exhaustive();
  return {
    type: limit.type,
    value: workspaceForm.values?.[wsProp] ?? limit.limitCfg,
    onChange: (cfg: WT.Limit) => workspaceForm.setValues({
      [wsProp]: cfg
    }),
    limitValue: limit.limit,
    readonly: limit.limitReadonly,
    currentAmount: limit.value
  };
};
export const EditLimit = t0 => {
  const $ = _c(26);
  const {
    type,
    onChange,
    readonly,
    value,
    currentAmount,
    limitValue
  } = t0;
  const t1 = `Limit ${type}`;
  let t2;
  if ($[0] !== limitValue || $[1] !== value.type) {
    t2 = mapEntries(WT.limitMd, _temp6).filter(l => l.value != "plan" || limitValue || value.type == "plan");
    $[0] = limitValue;
    $[1] = value.type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onChange || $[4] !== value.value) {
    t3 = v_0 => onChange({
      type: v_0 as WT.Limit['type'],
      value: v_0 == "plan" ? undefined : value.value
    });
    $[3] = onChange;
    $[4] = value.value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== readonly || $[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== value.type) {
    t4 = <Select label={t1} w="10em" data={t2} value={value.type} onChange={t3} disabled={readonly} />;
    $[6] = readonly;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = value.type;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== limitValue || $[13] !== onChange || $[14] !== value) {
    t5 = value.type != "no-limit" && <StringNumberInput w="6em" classNames={{
      section: "px-1 text-sm opacity-60"
    }} rightSection="users" value={value.type == "custom" ? value.value : limitValue} onChange={v_1 => onChange({
      ...value,
      value: v_1
    })} disabled={value.type != "custom"} />;
    $[12] = limitValue;
    $[13] = onChange;
    $[14] = value;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = <div className="flex gap-1 items-end my-4">{t4}{t5}</div>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== currentAmount || $[20] !== limitValue || $[21] !== type) {
    t7 = currentAmount != null && limitValue != null && currentAmount >= limitValue && <Alert color="orange" title={<div className="flex items-center gap-2"><HiExclamation /><span>You have reached your set limit</span></div>}>New {type} will be prevented form joining this workspace.</Alert>;
    $[19] = currentAmount;
    $[20] = limitValue;
    $[21] = type;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6 || $[24] !== t7) {
    t8 = <div className="flex flex-col gap-2 items-start">{t6}{t7}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
function _temp(p) {
  return isEmpty(p) ? undefined : p;
}
function _temp2(p_0) {
  return isEmpty(p_0) ? undefined : p_0;
}
function _temp3() {
  return <></>;
}
function _temp4(n) {
  return <Badge variant="light">{n}</Badge>;
}
function _temp5(n_0) {
  return n_0;
}
function _temp6(k, v) {
  return {
    label: v,
    value: k
  };
}